// Radar route: the main weekly pulse dashboard.

import React, { useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import { api } from '../lib/api';
import type { SnapshotData } from '../lib/api';
import { ThemeCard } from '../components/ThemeCard';
import { ActionTable } from '../components/ActionTable';
import { ProviderSentimentBar } from '../components/ProviderSentimentBar';
import { SentimentDistribution } from '../components/SentimentDistribution';
import { TotalMentionsCard } from '../components/TotalMentionsCard';
import { DataSourcesPanel } from '../components/DataSourcesPanel';
import { formatWeekRange } from '../components/Header';

interface RadarProps {
  lang: Lang;
  currentWeek: number | null;
  code: string;
}

export function Radar({ lang, currentWeek, code }: RadarProps) {
  const [snapshot, setSnapshot] = useState<SnapshotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const fetcher = currentWeek
      ? api.snapshots.byWeek(currentWeek)
      : api.snapshots.latest();

    fetcher
      .then(setSnapshot)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentWeek]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {t('loading', lang)}
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {error ?? t('noData', lang)}
      </div>
    );
  }

  // Build provider sentiment groups + filter to providers with any signal
  const signalsByProvider: Record<string, typeof snapshot.signals> = {};
  for (const sig of snapshot.signals) {
    if (!signalsByProvider[sig.provider_id]) signalsByProvider[sig.provider_id] = [];
    signalsByProvider[sig.provider_id].push(sig);
  }

  const sortedProviders = [...snapshot.providers].sort((a, b) => {
    const aTotal = (signalsByProvider[a.id] ?? []).reduce((s, x) => s + x.mention_count, 0);
    const bTotal = (signalsByProvider[b.id] ?? []).reduce((s, x) => s + x.mention_count, 0);
    return bTotal - aTotal;
  });

  const hasPriorWeek = snapshot.has_prior_week === true;
  const weekNum = snapshot.week_number % 100;
  const weekRange = formatWeekRange(snapshot.iso_date);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">

      {/* How to read — framing block. Establishes purpose, cadence, signal vs noise.
          Plain factual text, no marketing. Always visible. */}
      <section className="card p-4 border-neutral-200">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
            {t('howToRead', lang)}
          </h2>
          <span className="text-xs text-neutral-500 tabular-nums">
            Hafta {weekNum} · {weekRange}
          </span>
        </div>
        <div className="space-y-2 text-xs text-neutral-600 leading-relaxed">
          <p>{t('howToReadPurpose', lang)}</p>
          <p>{t('howToReadSignal', lang)}</p>
          <p className="text-neutral-500">{t('howToReadCadence', lang)}</p>
          <p className="mt-3 pl-3 border-l-2 border-amber-300 text-neutral-600">{t('howToReadHistory', lang)}</p>
        </div>
      </section>

      {/* Release note — what changed in this version, what is still weak, what is
          next. Positioned as a product update so the team can see the panel is
          maintained, and so known weaknesses are stated openly rather than hidden. */}
      <section className="card p-4 border-neutral-200">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
            {t('versionNote', lang)}
          </h2>
          <span className="text-xs text-neutral-500 tabular-nums">
            {t('versionNoteVersion', lang)}
          </span>
        </div>
        <div className="space-y-2 text-xs text-neutral-600 leading-relaxed">
          <p>{t('versionNoteFixed', lang)}</p>
          <p className="pl-3 border-l-2 border-amber-300">{t('versionNoteKnown', lang)}</p>
          <p className="text-neutral-500">{t('versionNoteNext', lang)}</p>
        </div>
      </section>

      {/* Stats row: total mentions with source breakdown. Per-severity theme
          counts removed — the Weekly Pulse cards below already carry each theme
          with its severity badge, so the count boxes were redundant clutter. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TotalMentionsCard
          totalMentions={snapshot.total_mentions ?? 0}
          signals={snapshot.signals}
          dataSources={snapshot.data_sources}
          providers={snapshot.providers}
          lang={lang}
        />
      </div>

      {/* Themes */}
      <section>
        <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
          {t('weeklyPulse', lang)}
        </h2>
        {snapshot.themes.length === 0 ? (
          <div className="card p-6 text-neutral-500 text-sm">{t('noData', lang)}</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {snapshot.themes.map((theme, i) => (
              <ThemeCard key={i} theme={theme} lang={lang} code={code} hasPriorWeek={hasPriorWeek} />
            ))}
          </div>
        )}
      </section>

      {/* Sentiment distribution */}
      <SentimentDistribution data={snapshot.sentiment_distribution} lang={lang} />

      {/* Provider sentiment */}
      <section>
        <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
          {t('providerSentiment', lang)}
        </h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {sortedProviders.map((provider) => (
            <ProviderSentimentBar
              key={provider.id}
              provider={provider}
              signals={signalsByProvider[provider.id] ?? []}
              lang={lang}
              code={code}
            />
          ))}
        </div>
      </section>

      {/* Actions */}
      <section>
        <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
          {t('actionRecommendations', lang)}
        </h2>
        <ActionTable actions={snapshot.actions} lang={lang} />
      </section>

      {/* Data sources transparency panel */}
      <DataSourcesPanel
        dataSources={snapshot.data_sources}
        pipelineStats={snapshot.pipeline_stats}
        news={snapshot.news}
        providers={snapshot.providers}
        signals={snapshot.signals}
        lang={lang}
      />

      {/* What unlocks next week — only rendered on the first cycle (no prior week).
          Frames the dashboard as a growing source of truth, never as 'incomplete'. */}
      {!hasPriorWeek && (
        <section className="card p-4 border-neutral-200">
          <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
            {t('nextWeekNote', lang)}
          </h2>
          <p className="text-xs text-neutral-600 mb-3 leading-relaxed">
            {lang === 'tr'
              ? `Bu, Hafta ${weekNum} (${weekRange}) için ilk çalışmadır. Karşılaştıracak önceki hafta henüz olmadığı için haftalık değişim ok ve etiketleri gizlendi.`
              : `This is the first run for week ${weekNum} (${weekRange}). Because there is no prior week to compare to yet, week-over-week arrows and labels are hidden.`}
          </p>
          <p className="text-xs text-neutral-500 mb-2">{t('nextWeekUnlocks', lang)}</p>
          <ul className="text-xs text-neutral-600 space-y-1 pl-4">
            <li className="list-disc list-outside marker:text-neutral-500">{t('nextWeekItem1', lang)}</li>
            <li className="list-disc list-outside marker:text-neutral-500">{t('nextWeekItem2', lang)}</li>
            <li className="list-disc list-outside marker:text-neutral-500">{t('nextWeekItem3', lang)}</li>
            <li className="list-disc list-outside marker:text-neutral-500">{t('nextWeekItem4', lang)}</li>
          </ul>
        </section>
      )}
    </div>
  );
}
